import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { api, mediaUrl } from '@/lib/api';
import { Play, Pause, Square, X, Bell, ShoppingBag, ArrowRight, Newspaper } from 'lucide-react';
import { useGlobalAudio } from '@/components/GlobalAudioPlayer';

/**
 * FeaturedHeadliner
 * -----------------
 * Replaces the fixed "Now Playing" hero card. Fetches the configured
 * headliner (live or draft) from /api/homepage/featured and renders a
 * per-content-type experience:
 *
 *   • music / podcast   → Play / Pause / Stop controls that drive the global
 *                         audio player; the card itself links into the channel.
 *   • article / mobile_read
 *                       → When preview_enabled, opens a premium preview modal
 *                         (featured image, title, preview copy, Buy Now,
 *                         Remind Me Later, Close). Otherwise navigates to the
 *                         configured detail route.
 *   • product / artwork / video
 *                       → Navigates to the configured CTA / detail route.
 *
 * Props:
 *   source: 'live' | 'draft'  — which blueprint to resolve (default 'live')
 *   configOverride            — optional; when provided, skips the network
 *                               fetch and uses this featured_headliner blob
 *                               directly (used by the Homepage Director's
 *                               in-admin live preview).
 */
export default function FeaturedHeadliner({ source = 'live', configOverride }) {
  const [state, setState] = useState({ loading: true, config: null, resolved: null });
  const navigate = useNavigate();
  const { current, isPlaying, togglePlay, setQueueAndPlay, stop } = useGlobalAudio();
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // When a config override is provided (Homepage Director preview), fetch
    // only the resolved-content preview from the backend using the override's
    // content_type/content_id when manual, otherwise from the live resolver.
    if (configOverride) {
      // Simplest path: reuse the resolver on the LIVE blueprint but visually
      // re-title/re-crop using the override. For manual mode with an ID, do a
      // targeted fetch.
      (async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
          const { data } = await api.get(`/homepage/featured?src=${source === 'draft' ? 'draft' : 'live'}`);
          if (!cancelled) setState({ loading: false, config: configOverride, resolved: data?.resolved || null });
        } catch (e) {
          if (!cancelled) setState({ loading: false, config: configOverride, resolved: null });
        }
      })();
    } else {
      (async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
          const { data } = await api.get(`/homepage/featured?src=${source === 'draft' ? 'draft' : 'live'}`);
          if (!cancelled) setState({ loading: false, config: data?.config || null, resolved: data?.resolved || null });
        } catch (e) {
          if (!cancelled) setState({ loading: false, config: null, resolved: null });
        }
      })();
    }
    return () => { cancelled = true; };
  }, [source, configOverride]);

  const { loading, config, resolved } = state;
  if (loading) return <FeaturedHeadlinerSkeleton />;
  if (!config || config.enabled === false) return null;

  const title = config.section_title || 'Featured';
  const contentType = resolved?.content_type || null;
  const item = resolved?.item || null;

  // Empty state — no matching content selected yet.
  if (!item) {
    return (
      <div className="pg-card relative overflow-hidden p-5 sm:p-6" data-testid="featured-headliner-empty" style={{ borderColor: 'rgba(226,199,122,0.28)' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="pg-pulse-ring" />
          <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold uppercase">{title}</span>
        </div>
        <p className="font-serif text-lg text-[var(--pg-ink)] leading-snug">The stage is being set.</p>
        <p className="text-xs text-[var(--pg-ink-muted)] mt-2">Assign a featured item from Experience Manager → Homepage Director.</p>
      </div>
    );
  }

  // ─── Music / Podcast ──────────────────────────────────────────────────────
  if (contentType === 'music' || contentType === 'podcast') {
    const isPlayable = item.file_url || (item.source_type === 'embed' && item.embed_url);
    const isThis = current && current.id === item.id;
    const playHere = () => {
      if (!isPlayable) return;
      if (isThis) { togglePlay(); return; }
      setQueueAndPlay([item], 0);
    };
    const stopHere = () => { if (isThis) stop(); };
    const channelHref = contentType === 'podcast' ? '/podcasts' : '/music';
    return (
      <div
        className="pg-card relative overflow-hidden p-5 sm:p-6 group"
        data-testid="featured-headliner"
        data-content-type={contentType}
        style={{ borderColor: 'rgba(25,194,142,0.35)', boxShadow: '0 0 30px rgba(25,194,142,0.10), 0 18px 60px rgba(0,0,0,0.55)' }}
      >
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <span className="pg-pulse-ring" />
            <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-emerald uppercase">{title}</span>
          </div>
          <Link
            to={channelHref}
            className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] hover:text-[var(--pg-emerald)] uppercase"
            data-testid="featured-headliner-open-channel"
          >
            Open channel →
          </Link>
        </div>
        <FeaturedArtworkAndCopy item={item} config={config} contentType={contentType} />
        <div className="mt-5 flex items-center justify-center gap-4">
          <button
            data-testid="featured-headliner-play"
            aria-label={isThis && isPlaying ? 'Pause' : 'Play'}
            onClick={playHere}
            disabled={!isPlayable}
            className="h-12 w-12 rounded-full border border-[rgba(25,194,142,0.7)] text-[var(--pg-emerald)] flex items-center justify-center hover:bg-[rgba(25,194,142,0.10)] disabled:opacity-50 transition-colors"
            style={{ boxShadow: '0 0 18px rgba(25,194,142,0.25), inset 0 0 14px rgba(25,194,142,0.15)' }}
          >
            {isThis && isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
          <button
            data-testid="featured-headliner-stop"
            aria-label="Stop"
            onClick={stopHere}
            disabled={!isThis}
            className="h-10 w-10 rounded-full border border-[var(--pg-border-2)] text-[var(--pg-ink-muted)] flex items-center justify-center hover:text-[var(--pg-ink)] disabled:opacity-40"
          >
            <Square size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ─── Article / Mobile Read ────────────────────────────────────────────────
  if (contentType === 'article' || contentType === 'mobile_read') {
    // Item-level override: for mobile reads, preview_enabled=false on the item
    // itself forces navigation to detail even if the headliner globally allows previews.
    const itemPreviewAllowed = contentType === 'mobile_read'
      ? item.preview_enabled !== false
      : true;
    const previewEnabled = !!config.preview_enabled && itemPreviewAllowed;
    const detailHref = contentType === 'article'
      ? (item.slug ? `/on-my-mind/${item.slug}` : '/on-my-mind')
      : '/mobile-reads';
    const openCard = () => {
      if (previewEnabled) setPreviewOpen(true);
      else navigate(detailHref);
    };
    return (
      <>
        <button
          type="button"
          onClick={openCard}
          data-testid="featured-headliner"
          data-content-type={contentType}
          className="pg-card relative overflow-hidden p-5 sm:p-6 w-full text-left group"
          style={{ borderColor: 'rgba(226,199,122,0.35)', boxShadow: '0 0 30px rgba(226,199,122,0.10), 0 18px 60px rgba(0,0,0,0.55)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold uppercase">{title}</span>
          </div>
          <FeaturedArtworkAndCopy item={item} config={config} contentType={contentType} />
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-mono tracking-widest pg-text-glow-gold uppercase">
            {previewEnabled ? 'Preview' : 'Open'} <ArrowRight size={12} />
          </div>
        </button>
        <FeaturedReadPreviewModal
          open={previewOpen && previewEnabled}
          item={item}
          contentType={contentType}
          onClose={() => setPreviewOpen(false)}
        />
      </>
    );
  }

  // ─── Product / Artwork / Video ────────────────────────────────────────────
  const isProduct = contentType === 'product';
  const isVideo = contentType === 'video';
  const detailHref = isProduct
    ? `/vault/${item.id}`
    : isVideo
      ? item.embed_url || '/watch'
      : `/collect`;
  const isExternal = isVideo && detailHref?.startsWith('http');
  const ctaLabel = isProduct ? 'View product' : isVideo ? 'Watch' : 'View';
  const CardWrapper = ({ children }) => (
    isExternal
      ? <a href={detailHref} target="_blank" rel="noreferrer" data-testid="featured-headliner" data-content-type={contentType} className="pg-card relative overflow-hidden p-5 sm:p-6 block group" style={{ borderColor: 'rgba(255,62,165,0.30)' }}>{children}</a>
      : <Link to={detailHref} data-testid="featured-headliner" data-content-type={contentType} className="pg-card relative overflow-hidden p-5 sm:p-6 block group" style={{ borderColor: 'rgba(255,62,165,0.30)' }}>{children}</Link>
  );
  return (
    <CardWrapper>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-pink uppercase">{title}</span>
      </div>
      <FeaturedArtworkAndCopy item={item} config={config} contentType={contentType} />
      <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-mono tracking-widest pg-text-glow-pink uppercase">
        {ctaLabel} <ArrowRight size={12} />
      </div>
    </CardWrapper>
  );
}

/* --------------------------- Building blocks ---------------------------- */

function FeaturedArtworkAndCopy({ item, config, contentType }) {
  const imgUrl = item.primary_image_url || item.cover_url || item.image_url || item.poster_url;
  const focalX = config.image_focal_x_pct ?? 50;
  const focalY = config.image_focal_y_pct ?? 50;
  const objectFit = config.image_crop === 'contain' ? 'contain' : 'cover';
  return (
    <div className="flex items-start gap-4">
      <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg overflow-hidden border border-[var(--pg-border-2)] bg-[var(--pg-navy-900)] shrink-0">
        {imgUrl ? (
          <img
            src={mediaUrl(imgUrl)}
            alt={item.title || ''}
            className="w-full h-full"
            style={{ objectFit, objectPosition: `${focalX}% ${focalY}%` }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--pg-ink-faint)]">
            {contentType === 'article' || contentType === 'mobile_read' ? <Newspaper size={18} /> : contentType === 'product' ? <ShoppingBag size={18} /> : '♪'}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-lg text-[var(--pg-ink)] leading-tight line-clamp-2">{item.title}</p>
        {(item.subtitle || item.description || item.excerpt) && (
          <p className="text-xs text-[var(--pg-ink)] mt-1 line-clamp-3 whitespace-pre-line">
            {stripHtml(item.excerpt || item.subtitle || item.description || '').slice(0, 220)}
          </p>
        )}
      </div>
    </div>
  );
}

function stripHtml(s) {
  if (!s) return '';
  const t = String(s);
  if (!/[<>]/.test(t)) return t;
  return t.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function FeaturedHeadlinerSkeleton() {
  return (
    <div className="pg-card p-5 sm:p-6" data-testid="featured-headliner-skeleton">
      <div className="h-3 w-24 bg-[var(--pg-navy-800)] rounded animate-pulse mb-4" />
      <div className="flex items-start gap-4">
        <div className="h-20 w-20 rounded-lg bg-[var(--pg-navy-800)] animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 bg-[var(--pg-navy-800)] rounded animate-pulse" />
          <div className="h-3 w-full bg-[var(--pg-navy-800)] rounded animate-pulse" />
          <div className="h-3 w-2/3 bg-[var(--pg-navy-800)] rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

/* ----------------- Article / Mobile Read Preview Modal ------------------ */

function FeaturedReadPreviewModal({ open, onClose, item, contentType }) {
  const [reminded, setReminded] = useState(false);
  if (!open || !item) return null;
  const imgUrl = item.primary_image_url || item.cover_url || item.image_url;
  const isMobileRead = contentType === 'mobile_read';
  const detailHref = isMobileRead ? '/mobile-reads' : (item.slug ? `/on-my-mind/${item.slug}` : '/on-my-mind');
  const buyPath = item.product_id ? `/checkout/${item.product_id}` : null;
  const remindMe = () => {
    try { localStorage.setItem(`pg:remind:${contentType}:${item.id}`, String(Date.now())); } catch (_) {}
    setReminded(true);
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="featured-read-preview-modal"
        >
          <div className="absolute inset-0 bg-[rgba(4,6,12,0.85)] backdrop-blur-md" />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto pg-scroll pg-card"
            onClick={(e) => e.stopPropagation()}
          >
            {imgUrl && (
              <div className="w-full h-64 sm:h-80 overflow-hidden">
                <img src={mediaUrl(imgUrl)} alt={item.title || ''} className="w-full h-full object-cover" />
              </div>
            )}
            <button
              aria-label="Close preview"
              onClick={onClose}
              data-testid="featured-read-preview-close"
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-[rgba(0,0,0,0.55)] text-[var(--pg-ink)] flex items-center justify-center hover:bg-[rgba(0,0,0,0.75)]"
            >
              <X size={16} />
            </button>
            <div className="p-6 sm:p-8">
              <p className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold uppercase">
                {isMobileRead ? 'Mobile Read' : 'Article'}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 tracking-[0.03em] text-[var(--pg-ink)]" data-testid="featured-read-preview-title">
                {item.title}
              </h2>
              {(item.preview_body || item.excerpt || item.subtitle) && (
                <p className="text-[var(--pg-ink)] text-base leading-relaxed mt-4 whitespace-pre-line" data-testid="featured-read-preview-body">
                  {stripHtml(item.preview_body || item.excerpt || item.subtitle || '')}
                </p>
              )}
              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {buyPath && (
                  <Link to={buyPath} data-testid="featured-read-preview-buy" className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2.5 text-xs font-mono tracking-widest inline-flex items-center justify-center gap-2">
                    <ShoppingBag size={13} /> BUY NOW
                  </Link>
                )}
                <Link to={detailHref} className="pg-btn-neon rounded-[var(--pg-radius-md)] px-4 py-2.5 text-xs font-mono tracking-widest inline-flex items-center justify-center gap-2" data-testid="featured-read-preview-open">
                  READ FULL <ArrowRight size={13} />
                </Link>
                <button onClick={remindMe} disabled={reminded} data-testid="featured-read-preview-remind" className="pg-btn-ghost rounded-[var(--pg-radius-md)] px-4 py-2.5 text-xs font-mono tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <Bell size={13} /> {reminded ? 'REMINDED' : 'REMIND ME LATER'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
